import React, { useEffect, useRef, useState } from 'react';
import './MapComponent.css';
import PointWithRadius from './PointWithRadius';
import { useBuilding } from '../../../context/BuildingContext';
import axiosInstance, { BASE_API_URL } from '../../../utils/router/axiosInstance';
import { getMapRenderSize, isOutsidePlan } from '../../../utils/mapCoordinates';
import { getMapCalibration } from '../../../utils/mapCalibration';
import { logger } from '../../../utils/logger';

/**
 * «2 точки», но «5 точек». Единственное число не нужно: строка с ним написана
 * словами отдельно («Одна Wi-Fi точка…») — цифра «1» в предупреждении читается
 * хуже слова.
 */
const dotsWord = (n) => {
  const tail = n % 100;
  if (tail >= 11 && tail <= 14) return 'Wi-Fi точек';
  return (n % 10 >= 2 && n % 10 <= 4) ? 'Wi-Fi точки' : 'Wi-Fi точек';
};

const MapComponent = ({ employees, building_id }) => {
  const { selectedFloor } = useBuilding();
  const containerRef = useRef(null);
  const [mapImage, setMapImage] = useState('');
  const [floorSize, setFloorSize] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [calibration, setCalibration] = useState(getMapCalibration({ buildingId: building_id, floor: selectedFloor }));
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    /**
     * WEB-18: обе ветки этого эффекта меряют ОДНО И ТО ЖЕ — содержимое.
     *
     * `clientWidth` включает внутренние поля контейнера (у карты это
     * clamp(12px, 1.5vw, 20px) с каждой стороны), а ResizeObserver ниже отдаёт
     * `contentRect` — БЕЗ них. То есть две ветки считали РАЗНОЕ, и по этой
     * ширине расставляются координаты меток.
     *
     * 🔴 Честно о цене: там, где ResizeObserver есть, он отрабатывает вторым и
     * его (правильное) значение побеждает — проверено мутантом, метки стоят на
     * плане и со старым кодом. Значит это не починка видимого дефекта, а снятие
     * РАСХОЖДЕНИЯ: в запасной ветке (старые Safari и вебвью без RO — она тут же
     * ниже) единственным источником оставался `clientWidth`, и там метки
     * считались по ширине, завышенной на поля, а картинку плана при этом
     * ужимает `max-width: 100%`.
     */
    const updateContainerSize = () => {
      const style = window.getComputedStyle(element);
      const padX = (parseFloat(style.paddingLeft) || 0)
        + (parseFloat(style.paddingRight) || 0);
      const padY = (parseFloat(style.paddingTop) || 0)
        + (parseFloat(style.paddingBottom) || 0);

      setContainerSize({
        width: Math.max(0, element.clientWidth - padX),
        height: Math.max(0, element.clientHeight - padY),
      });
    };

    updateContainerSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateContainerSize);

      return () => window.removeEventListener('resize', updateContainerSize);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const [entry] = entries;

      if (!entry) {
        return;
      }

      setContainerSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const mapSize = getMapRenderSize({
    sourceWidth: imageSize.width,
    sourceHeight: imageSize.height,
    containerWidth: containerSize.width,
    containerHeight: containerSize.height,
  });

  useEffect(() => {
    const handleCalibrationUpdate = (event) => {
      const updatedBuildingId = String(event.detail?.buildingId);
      const updatedFloor = Number(event.detail?.floor);

      if (updatedBuildingId !== String(building_id) || updatedFloor !== Number(selectedFloor)) {
        return;
      }

      setCalibration(
        getMapCalibration({
          buildingId: building_id,
          floor: selectedFloor,
          fallbackPlanWidth: floorSize.x,
          fallbackPlanHeight: floorSize.y,
        }),
      );
    };

    window.addEventListener('map-calibration-updated', handleCalibrationUpdate);

    return () => window.removeEventListener('map-calibration-updated', handleCalibrationUpdate);
  }, [building_id, selectedFloor, floorSize.x, floorSize.y]);

  useEffect(() => {
    const fetchData = async () => {
      if (localStorage.getItem('current_floor_amount') < localStorage.getItem('selectedFloor')) {
        localStorage.setItem('selectedFloor', 1);
      }
      try {
        const mapResponse = await axiosInstance.get(`website/map/${building_id}?floor=${selectedFloor}`);
        const floorData = mapResponse.data[0];

        if (!floorData || floorData.floor_plan === '404') {
          // 🔴 Размеры этажа сбрасываются ТОЖЕ (ревью Codex WEB-18, Medium):
          // без этого на этаже без плана оставались размеры ПРЕДЫДУЩЕГО, и по
          // ним считалось предупреждение «точки вне этажа» — про этаж, которого
          // на экране нет.
          logger.warn('Карта этажа не найдена');
          setMapImage('');
          setImageSize({ width: 0, height: 0 });
          setFloorSize({ x: 0, y: 0 });
          return;
        }

        const nextFloorSize = {
          x: Number(floorData.floor_size?.x) || 0,
          y: Number(floorData.floor_size?.y) || 0,
        };

        setMapImage(floorData.floor_plan);
        setImageSize({ width: 0, height: 0 });
        setFloorSize(nextFloorSize);
        setCalibration(
          getMapCalibration({
            buildingId: building_id,
            floor: selectedFloor,
            fallbackPlanWidth: nextFloorSize.x,
            fallbackPlanHeight: nextFloorSize.y,
          }),
        );
      } catch (error) {
        setMapImage('');
        setImageSize({ width: 0, height: 0 });
        setFloorSize({ x: 0, y: 0 });
        logger.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [building_id, selectedFloor]);

  logger.debug(employees, 'asdasd');

  const dots = employees?.dots ?? [];
  const people = employees?.users ?? [];

  // 🔴 WEB-18: сколько точек не помещается в заявленные размеры этажа. На бою
  // таких три из пяти (x до 9,9 и y до 6,1 при этаже 9×6), и до сих пор карта
  // об этом молчала: точка прижималась к краю плана и выглядела стоящей у
  // стены. Правило одно на всех — `isOutsidePlan`, им же метит себя сама точка.
  const outsideCount = dots.filter((dot) => isOutsidePlan({
    x: dot?.coordinates?.x,
    y: dot?.coordinates?.y,
    planWidth: calibration.planWidth,
    planHeight: calibration.planHeight,
  })).length;

  return (
    <div className="mapContainer" ref={containerRef}>
      {/* 🔴 Пустое состояние. Экран открывают ради вопроса «кто сейчас на
          объекте», и «никого нет» обязано читаться КАК ОТВЕТ. До этого пустая
          карта выглядела ровно так же, как не загрузившаяся, и отличить одно
          от другого было нельзя. Про точки молчим: их отсутствие — это
          настройка, а не событие смены. */}
      {mapImage && people.length === 0 && (
        <p className="mapEmpty" data-testid="map-empty">
          Сейчас на объекте никого нет
        </p>
      )}

      <div className="mapImageContainer" style={{ width: `${mapSize.width}px`, height: `${mapSize.height}px` }}>
        {mapImage && (
          <img
            src={`${BASE_API_URL}${mapImage}`}
            className="mapImage"
            alt="floorPlan"
            onLoad={(event) => {
              setImageSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              });
            }}
          />
        )}
        {employees?.users?.map((employee) => (
          <PointWithRadius
            planWidth={calibration.planWidth}
            planHeight={calibration.planHeight}
            calibration={calibration}
            entityType="employee"
            key={employee.id}
            mapWidth={mapSize.width}
            mapHeight={mapSize.height}
            x={employee.coordinates.x}
            y={employee.coordinates.y}
            radius={employee.coordinates.radius}
            users={employee}
          />
        ))}
        {employees?.dots?.map((employee) => (
          <PointWithRadius
            planWidth={calibration.planWidth}
            planHeight={calibration.planHeight}
            calibration={calibration}
            entityType="dot"
            key={employee.id}
            mapWidth={mapSize.width}
            mapHeight={mapSize.height}
            x={employee.coordinates.x}
            y={employee.coordinates.y}
            radius={employee.coordinates.radius}
            users={employee}
          />
        ))}
      </div>

      {/* 🔴 Легенда. Красный кружок — Wi-Fi точка, зелёный — человек; за всё
          время это не было написано НИГДЕ, и цвета приходилось угадывать.
          Показывается только то, что на карте действительно есть. */}
      {mapImage && (dots.length > 0 || people.length > 0) && (
        <div className="mapLegend" data-testid="map-legend">
          {dots.length > 0 && (
            <span className="mapLegendItem">
              <i className="mapLegendMark mapLegendDot" aria-hidden="true" />
              Wi-Fi точка
            </span>
          )}
          {people.length > 0 && (
            <span className="mapLegendItem">
              <i className="mapLegendMark mapLegendPerson" aria-hidden="true" />
              сотрудник
            </span>
          )}
        </div>
      )}

      {/* Гейт по `mapImage`: без плана говорить о «пределах этажа» не о чем. */}
      {mapImage && outsideCount > 0 && (
        <p className="mapWarn" data-testid="map-outside-warning" role="status">
          {outsideCount === 1
            ? 'Одна Wi-Fi точка задана за пределами этажа и прижата к краю плана — проверьте её координаты.'
            : `${outsideCount} ${dotsWord(outsideCount)} заданы за пределами этажа и прижаты к краю плана — проверьте их координаты.`}
        </p>
      )}
    </div>
  );
};

export default MapComponent;
